import ThemedSelect from '../../common/ThemedSelect';
import React, { useState, useEffect } from 'react';
import { X, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { parseISO } from 'date-fns';
import { toApiDateString } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';

const CATEGORIES = [
  'Calibration Failure',
  'Verification Failure',
  'Maintenance Finding',
  'Suspect Reading',
  'Seal Breach',
  'Operator Non-Compliance',
  'Other',
];

const SEVERITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const STATUS_OPTIONS = ['Open', 'InProgress', 'Resolved', 'Closed', 'Cancelled'];

const RE_VERIFY_CATEGORIES = ['Calibration Failure', 'Verification Failure'];
const RESOLVED_STATUSES = ['Resolved', 'Closed'];

const buildAutoNCRNumber = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const seq = Date.now().toString().slice(-6);
  return `NCR-${year}${month}-${seq}`;
};

const initialForm = {
  ncrNumber: '',
  assetId: '',
  category: '',
  severity: 'Medium',
  title: '',
  description: '',
  rootCause: '',
  correctiveAction: '',
  preventiveAction: '',
  assignedTo: '',
  dueDate: null,
  status: 'Open',
  resolvedDate: null,
  requiresReVerification: false,
  notes: '',
};

export const NCRModal = ({ record, assets = [], onClose, onSaved }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(record);

  useEffect(() => {
    if (record) {
      setForm({
        ncrNumber: record.ncr_number || '',
        assetId: record.asset_id || '',
        category: record.category || '',
        severity: record.severity || 'Medium',
        title: record.title || '',
        description: record.description || '',
        rootCause: record.root_cause || '',
        correctiveAction: record.corrective_action || '',
        preventiveAction: record.preventive_action || '',
        assignedTo: record.assigned_to || '',
        dueDate: record.due_date ? parseISO(record.due_date) : null,
        status: record.status || 'Open',
        resolvedDate: record.resolved_date ? parseISO(record.resolved_date) : null,
        requiresReVerification: record.requires_re_verification || false,
        notes: record.notes || '',
      });
    } else {
      setForm({ ...initialForm, ncrNumber: buildAutoNCRNumber() });
    }
    setErrors({});
  }, [record]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const errs = {};
    if (!form.ncrNumber.trim()) errs.ncrNumber = 'NCR Number is required';
    if (!form.assetId) errs.assetId = 'Asset is required';
    if (!form.category) errs.category = 'Category is required';
    if (!form.severity) errs.severity = 'Severity is required';
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ncr_number: form.ncrNumber.trim(),
        asset_id: form.assetId ? Number(form.assetId) : undefined,
        category: form.category,
        severity: form.severity,
        title: form.title.trim(),
        description: form.description.trim(),
        root_cause: form.rootCause.trim(),
        corrective_action: form.correctiveAction.trim(),
        preventive_action: form.preventiveAction.trim(),
        assigned_to: form.assignedTo.trim(),
        due_date: form.dueDate ? toApiDateString(form.dueDate) : null,
        status: form.status,
        resolved_date: form.resolvedDate ? toApiDateString(form.resolvedDate) : null,
        requires_re_verification: form.requiresReVerification,
        notes: form.notes.trim(),
      };

      if (isEdit) {
        await api.ncr.update(record.id, payload);
        toast.success('NCR updated');
      } else {
        await api.ncr.create(payload);
        toast.success('NCR raised successfully');
      }
      onSaved();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to save NCR';
      toast.error(msg);
      console.error('Error saving NCR:', error);
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ value, onChange, label }) => (
    <div
      className="flex items-center gap-2 cursor-pointer select-none"
      onClick={() => onChange(!value)}
    >
      {value ? (
        <ToggleRight className="w-8 h-5 text-teal-500" />
      ) : (
        <ToggleLeft className="w-8 h-5 text-slate-300" />
      )}
      <span className="text-sm text-slate-700">{label}</span>
    </div>
  );

  const showReVerify = RE_VERIFY_CATEGORIES.includes(form.category);
  const showResolvedDate = RESOLVED_STATUSES.includes(form.status);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {isEdit ? 'Edit Non-Conformance' : 'Raise Non-Conformance'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* NCR Number */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              NCR Number *
            </label>
            <input
              type="text"
              value={form.ncrNumber}
              onChange={(e) => set('ncrNumber', e.target.value)}
              className={`w-full px-3 py-2.5 border ${errors.ncrNumber ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm font-mono`}
              placeholder="e.g. NCR-2026-001"
            />
            {errors.ncrNumber && <p className="text-xs text-red-500 mt-1">{errors.ncrNumber}</p>}
          </div>

          {/* Asset & Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Asset *
              </label>
              <ThemedSelect
                value={form.assetId}
                onChange={(e) => set('assetId', e.target.value)}
                className={`w-full px-3 py-2.5 border ${errors.assetId ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select asset...</option>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name || a.asset_name}
                  </option>
                ))}
              </ThemedSelect>
              {errors.assetId && <p className="text-xs text-red-500 mt-1">{errors.assetId}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Category *
              </label>
              <ThemedSelect
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className={`w-full px-3 py-2.5 border ${errors.category ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select category...</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </ThemedSelect>
              {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
            </div>
          </div>

          {/* Severity & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Severity *
              </label>
              <ThemedSelect
                value={form.severity}
                onChange={(e) => set('severity', e.target.value)}
                className={`w-full px-3 py-2.5 border ${errors.severity ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select severity...</option>
                {SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </ThemedSelect>
              {errors.severity && <p className="text-xs text-red-500 mt-1">{errors.severity}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Status
              </label>
              <ThemedSelect
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s === 'InProgress' ? 'In Progress' : s}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Title *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              className={`w-full px-3 py-2.5 border ${errors.title ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              placeholder="Brief description of the non-conformance"
            />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Description *
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2.5 border ${errors.description ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none`}
              placeholder="Detailed description of the non-conformance..."
            />
            {errors.description && (
              <p className="text-xs text-red-500 mt-1">{errors.description}</p>
            )}
          </div>

          {/* Root Cause */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Root Cause
            </label>
            <textarea
              value={form.rootCause}
              onChange={(e) => set('rootCause', e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
              placeholder="Identified root cause of the non-conformance..."
            />
          </div>

          {/* Corrective & Preventive Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Corrective Action
              </label>
              <textarea
                value={form.correctiveAction}
                onChange={(e) => set('correctiveAction', e.target.value)}
                rows={3}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
                placeholder="Actions taken to correct the issue..."
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Preventive Action
              </label>
              <textarea
                value={form.preventiveAction}
                onChange={(e) => set('preventiveAction', e.target.value)}
                rows={3}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
                placeholder="Actions to prevent recurrence..."
              />
            </div>
          </div>

          {/* Assigned To & Due Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Assigned To
              </label>
              <input
                type="text"
                value={form.assignedTo}
                onChange={(e) => set('assignedTo', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="Responsible person"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Due Date
              </label>
              <DatePicker
                selected={form.dueDate}
                onChange={(date) => set('dueDate', date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              />
            </div>
          </div>

          {/* Resolved Date — shown only for Resolved/Closed status */}
          {showResolvedDate && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Resolved Date
              </label>
              <DatePicker
                selected={form.resolvedDate}
                onChange={(date) => set('resolvedDate', date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              />
            </div>
          )}

          {/* Requires Re-Verification toggle — shown for Calibration/Verification Failure */}
          {showReVerify && (
            <div className="pt-1">
              <Toggle
                value={form.requiresReVerification}
                onChange={(val) => set('requiresReVerification', val)}
                label="Requires Re-Verification"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
              placeholder="Any additional notes..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : isEdit ? 'Update NCR' : 'Raise NCR'}
          </button>
        </div>
      </div>
    </div>
  );
};
